import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import TransactionOverview from './components/TransactionOverview';
import StakeholderRoster from './components/StakeholderRoster';
import TimelineVisualization from './components/TimelineVisualization';
import DocumentRepository from './components/DocumentRepository';
import StakeholderCommunications from './components/StakeholderCommunications';
import ComplianceChecklist from './components/ComplianceChecklist';
import AuditTrail from './components/AuditTrail';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const TransactionDetails = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Mock transaction data
  const transactionData = {
    id: "TXN-2025-001",
    propertyName: "Sunset Boulevard Commercial Complex",
    address: "1234 Sunset Boulevard, Los Angeles, CA 90028",
    propertyImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop",
    status: "In Progress",
    startDate: "March 15, 2025",
    trlScore: 72,
    completedDocuments: 18,
    totalDocuments: 25,
    completedSignatures: 3,
    totalSignatures: 5,
    propertyType: "Commercial Office Building",
    squareFootage: "45,000",
    yearBuilt: "2018",
    value: "$12,500,000",
    tokenSupply: "12,500",
    expectedCompletion: "April 30, 2025"
  };

  const stakeholdersData = [
    {
      id: 1,
      name: "Sarah Mitchell",
      role: "Property Owner",
      email: "sarah.mitchell@email.com",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      status: "Active",
      documentsCompleted: 8,
      totalDocuments: 10
    },
    {
      id: 2,
      name: "David Chen",
      role: "Real Estate Lawyer",
      email: "david.chen@lawfirm.com",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      status: "Active",
      documentsCompleted: 5,
      totalDocuments: 7
    },
    {
      id: 3,
      name: "Maria Rodriguez",
      role: "Certified Engineer",
      email: "maria.rodriguez@engineering.com",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      status: "Pending",
      documentsCompleted: 2,
      totalDocuments: 4
    },
    {
      id: 4,
      name: "James Wilson",
      role: "Licensed Notary",
      email: "james.wilson@notary.com",
      avatar: "https://randomuser.me/api/portraits/men/52.jpg",
      status: "Active",
      documentsCompleted: 3,
      totalDocuments: 4
    }
  ];

  const milestonesData = [
    {
      id: 1,
      title: "Initial Documentation Submitted",
      description: "Property ownership documents and initial legal paperwork uploaded",
      date: "March 15, 2025",
      status: "completed",
      documents: ["Property Deed", "Title Insurance", "Survey Report"],
      stakeholders: ["Sarah Mitchell", "David Chen"]
    },
    {
      id: 2,
      title: "Legal Review Completed",
      description: "Legal team has reviewed and approved all submitted documentation",
      date: "March 20, 2025",
      status: "completed",
      documents: ["Legal Opinion Letter", "Due Diligence Report"],
      stakeholders: ["David Chen"]
    },
    {
      id: 3,
      title: "Engineering Assessment",
      description: "Property inspection and technical evaluation in progress",
      date: "March 25, 2025",
      status: "in-progress",
      progress: 65,
      documents: ["Structural Report", "Environmental Assessment"],
      stakeholders: ["Maria Rodriguez"]
    },
    {
      id: 4,
      title: "Financial Documentation",
      description: "Awaiting final financial statements and valuation reports",
      date: "March 30, 2025",
      status: "pending",
      documents: ["Appraisal Report", "Financial Statements"],
      stakeholders: ["Sarah Mitchell"]
    },
    {
      id: 5,
      title: "Final Notarization",
      description: "Document notarization and blockchain timestamp recording",
      date: "April 5, 2025",
      status: "pending",
      documents: ["Notarized Agreements", "Blockchain Certificates"],
      stakeholders: ["James Wilson"]
    }
  ];

  const documentsData = [
    {
      id: 1,
      name: "Property Deed - Original",
      description: "Official property ownership document with legal descriptions",
      category: "legal",
      type: "pdf",
      size: "2.4 MB",
      uploadedBy: "Sarah Mitchell",
      uploadDate: "March 15, 2025",
      status: "signed",
      version: 1,
      blockchainVerified: true,
      completedSignatures: 2,
      requiredSignatures: [
        { stakeholder: "Sarah Mitchell", completed: true },
        { stakeholder: "David Chen", completed: true }
      ]
    },
    {
      id: 2,
      name: "Environmental Impact Assessment",
      description: "Comprehensive environmental evaluation and compliance report",
      category: "technical",
      type: "pdf",
      size: "8.7 MB",
      uploadedBy: "Maria Rodriguez",
      uploadDate: "March 22, 2025",
      status: "pending-signature",
      version: 2,
      blockchainVerified: false,
      completedSignatures: 1,
      requiredSignatures: [
        { stakeholder: "Maria Rodriguez", completed: true },
        { stakeholder: "Sarah Mitchell", completed: false },
        { stakeholder: "David Chen", completed: false }
      ]
    },
    {
      id: 3,
      name: "Financial Statements Q4 2024",
      description: "Quarterly financial performance and asset valuation",
      category: "financial",
      type: "xlsx",
      size: "1.2 MB",
      uploadedBy: "Sarah Mitchell",
      uploadDate: "March 18, 2025",
      status: "uploaded",
      version: 1,
      blockchainVerified: false,
      completedSignatures: 0,
      requiredSignatures: [
        { stakeholder: "David Chen", completed: false }
      ]
    },
    {
      id: 4,
      name: "Title Insurance Policy",
      description: "Insurance coverage for property title and ownership rights",
      category: "legal",
      type: "pdf",
      size: "3.1 MB",
      uploadedBy: "David Chen",
      uploadDate: "March 16, 2025",
      status: "signed",
      version: 1,
      blockchainVerified: true,
      completedSignatures: 3,
      requiredSignatures: [
        { stakeholder: "Sarah Mitchell", completed: true },
        { stakeholder: "David Chen", completed: true },
        { stakeholder: "James Wilson", completed: true }
      ]
    }
  ];

  const messagesData = [
    {
      id: 1,
      sender: "David Chen",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      content: "I\'ve reviewed the environmental assessment. There are a few minor issues that need to be addressed before we can proceed with the final approval.",
      timestamp: new Date(Date.now() - 1800000),
      thread: "technical",
      isOwn: false,
      read: true
    },
    {
      id: 2,
      sender: "You",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      content: "Thanks for the review, David. Can you specify which sections need revision? I want to make sure we address everything properly.",
      timestamp: new Date(Date.now() - 1200000),
      thread: "technical",
      isOwn: true,
      read: true
    },
    {
      id: 3,
      sender: "Maria Rodriguez",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      content: "I can provide additional technical documentation by tomorrow. The structural analysis is complete and shows excellent building integrity.",
      timestamp: new Date(Date.now() - 900000),
      thread: "technical",
      isOwn: false,
      read: false,
      attachment: {
        type: "document",
        name: "structural-analysis-v2.pdf"
      }
    },
    {
      id: 4,
      sender: "James Wilson",
      avatar: "https://randomuser.me/api/portraits/men/52.jpg",
      content: "Please schedule the final document signing session. I'm available next week for the notarization process.",
      timestamp: new Date(Date.now() - 600000),
      thread: "legal",
      isOwn: false,
      read: false
    }
  ];

  const checklistData = [
    {
      id: 1,
      title: "Property Ownership Verification",
      description: "Verify current ownership status and legal title",
      category: "legal-documentation",
      role: "legal",
      status: "completed",
      priority: "high",
      assignedTo: "David Chen",
      dueDate: "March 20, 2025",
      relatedDocuments: ["Property Deed", "Title Insurance"],
      notes: "Verified through county records and title company"
    },
    {
      id: 2,
      title: "Environmental Compliance Check",
      description: "Ensure property meets all environmental regulations",
      category: "compliance-verification",
      role: "technical",
      status: "in-progress",
      priority: "high",
      assignedTo: "Maria Rodriguez",
      dueDate: "March 28, 2025",
      relatedDocuments: ["Environmental Assessment"],
      notes: "Awaiting final soil contamination report"
    },
    {
      id: 3,
      title: "Financial Statement Audit",
      description: "Review and validate all financial documentation",
      category: "financial-verification",
      role: "financial",
      status: "pending",
      priority: "medium",
      assignedTo: "External Auditor",
      dueDate: "April 1, 2025",
      relatedDocuments: ["Financial Statements", "Tax Returns"]
    },
    {
      id: 4,
      title: "Zoning Compliance Verification",
      description: "Confirm property usage aligns with zoning regulations",
      category: "compliance-verification",
      role: "compliance",
      status: "completed",
      priority: "medium",
      assignedTo: "City Planning Office",
      dueDate: "March 25, 2025",
      relatedDocuments: ["Zoning Certificate"]
    },
    {
      id: 5,
      title: "Insurance Coverage Review",
      description: "Validate adequate insurance coverage for tokenization",
      category: "financial-verification",
      role: "financial",
      status: "overdue",
      priority: "high",
      assignedTo: "Insurance Broker",
      dueDate: "March 22, 2025",
      relatedDocuments: ["Insurance Policies"]
    }
  ];

  const auditLogsData = [
    {
      id: 1,
      description: "Document uploaded: Environmental Impact Assessment v2",
      action: "upload",
      category: "document",
      userName: "Maria Rodriguez",
      userAvatar: "https://randomuser.me/api/portraits/women/28.jpg",
      timestamp: new Date(Date.now() - 3600000),
      blockchainHash: "0x1a2b3c4d5e6f7890abcdef1234567890",
      details: "File size: 8.7 MB, Format: PDF",
      metadata: {
        ipAddress: "192.168.1.100",
        userAgent: "Chrome/120.0.0.0"
      }
    },
    {
      id: 2,
      description: "Digital signature applied to Property Deed",
      action: "sign",
      category: "signature",
      userName: "David Chen",
      userAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
      timestamp: new Date(Date.now() - 7200000),
      blockchainHash: "0x9876543210fedcba0987654321abcdef",
      details: "Signature verified using private key cryptography"
    },
    {
      id: 3,
      description: "Stakeholder invited to transaction",
      action: "invite",
      category: "system",
      userName: "Sarah Mitchell",
      userAvatar: "https://randomuser.me/api/portraits/women/32.jpg",
      timestamp: new Date(Date.now() - 10800000),
      details: "Invited: james.wilson@notary.com as Licensed Notary"
    },
    {
      id: 4,
      description: "Comment added to Environmental Assessment",
      action: "comment",
      category: "communication",
      userName: "David Chen",
      userAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
      timestamp: new Date(Date.now() - 14400000),
      details: "Review comment: Minor issues identified in sections 3.2 and 4.1"
    },
    {
      id: 5,
      description: "Document approved: Title Insurance Policy",
      action: "approve",
      category: "document",
      userName: "James Wilson",
      userAvatar: "https://randomuser.me/api/portraits/men/52.jpg",
      timestamp: new Date(Date.now() - 18000000),
      blockchainHash: "0xabcdef1234567890fedcba0987654321",
      details: "Final approval granted with notarization"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'documents', label: 'Documents', icon: 'FolderOpen' },
    { id: 'communications', label: 'Communications', icon: 'MessageCircle' },
    { id: 'compliance', label: 'Compliance', icon: 'CheckSquare' },
    { id: 'audit', label: 'Audit Trail', icon: 'FileSearch' }
  ];

  const handleUploadDocument = () => {
    setShowUploadModal(true);
  };

  const handleInviteStakeholder = () => {
    setShowInviteModal(true);
  };

  const handlePreviewDocument = (document) => {
    console.log('Preview document:', document);
  };

  const handleDownloadDocument = (document) => {
    console.log('Download document:', document);
  };

  const handleSendMessage = (messageData) => {
    console.log('Send message:', messageData);
  };

  const handleUpdateChecklistItem = (itemId, updates) => {
    console.log('Update checklist item:', itemId, updates);
  };

  const handleExportLogs = () => {
    console.log('Export audit logs');
  };

  useEffect(() => {
    document.title = `Transaction Details - ${transactionData?.propertyName} | PropertyChain`;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <main className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Quick Actions Bar */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                iconName="ArrowLeft" 
                iconPosition="left"
                onClick={() => navigate('/transaction-dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                iconName="Share" 
                iconPosition="left"
              >
                Share Transaction
              </Button>
              <Button 
                variant="default" 
                iconName="PenTool" 
                iconPosition="left"
                onClick={() => navigate('/digital-signature')}
              >
                Request Signature
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <TransactionOverview transaction={transactionData} />
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <StakeholderRoster 
                    stakeholders={stakeholdersData} 
                    onInviteStakeholder={handleInviteStakeholder}
                  />
                  <TimelineVisualization milestones={milestonesData} />
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <DocumentRepository 
                documents={documentsData}
                onUploadDocument={handleUploadDocument}
                onPreviewDocument={handlePreviewDocument}
                onDownloadDocument={handleDownloadDocument}
              />
            )}

            {activeTab === 'communications' && (
              <StakeholderCommunications 
                messages={messagesData}
                onSendMessage={handleSendMessage}
              />
            )}

            {activeTab === 'compliance' && (
              <ComplianceChecklist 
                checklistItems={checklistData}
                onUpdateItem={handleUpdateChecklistItem}
              />
            )}

            {activeTab === 'audit' && (
              <AuditTrail 
                auditLogs={auditLogsData}
                onExportLogs={handleExportLogs}
              />
            )}
          </div>
        </div>
      </main>
      {/* Mobile Tab Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-40">
        <div className="flex justify-around py-2">
          {tabs?.slice(0, 4)?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex flex-col items-center space-y-1 py-2 px-3 rounded-md transition-smooth ${
                activeTab === tab?.id
                  ? 'text-primary bg-primary/10' :'text-muted-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={20} />
              <span className="text-xs font-medium">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;